import { useState } from 'react';
import { Field } from './ui.jsx';
import Icon from './Icon.jsx';
import MapPicker from './LazyMapPicker.jsx';
import { api } from '../lib/api.js';

/**
 * Chooses where the nurse should go for one visit.
 *
 * Three cases, because they need very different amounts of typing:
 *  - `saved`   the patient's own address, already on file — nothing to enter,
 *              so the booking wizard's address step can be skipped outright.
 *  - `current` the patient is standing at the visit location — one tap reads
 *              the device position and reverse-geocodes it, again skipping the
 *              step. Offered only where it makes sense (`allowCurrent`), i.e.
 *              not when an administrator books on someone else's behalf.
 *  - `other`   the visit is for a family member somewhere else. This is the
 *              only case that needs a pin on a map, so it is the only case that
 *              renders the picker — an unnecessary map on every booking would
 *              be a lot of screen and network for an address already known.
 *
 * `value` is `{ mode, address, city, lat, lng }` and `onChange(patch)` merges
 * partial updates. `onResolved()` fires when a one-tap mode has produced a
 * usable address, which lets the caller jump straight past the step.
 */

const HERE_HINT = 'We use your device location once, to place the pin. It is not tracked afterwards.';

export default function VisitLocation({
  value,
  onChange,
  saved,
  allowCurrent = true,
  savedLabel = 'My saved address',
  onResolved,
}) {
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState('');
  const [editingSaved, setEditingSaved] = useState(false);

  const hasSaved = Boolean(saved?.address?.trim());

  const choices = [
    hasSaved && { id: 'saved', icon: 'home', name: savedLabel, hint: 'Nothing to fill in' },
    allowCurrent && { id: 'current', icon: 'navigation', name: "I'm at the visit location", hint: 'Use my current position' },
    { id: 'other', icon: 'map', name: 'Somewhere else', hint: 'For a family member — pick on the map' },
  ].filter(Boolean);

  const choose = (mode) => {
    setStatus('');
    if (mode === 'saved') {
      // Re-applying the saved address clears any pin: the profile address is
      // text-only, and a stale pin would send the nurse to the previous spot.
      onChange({ mode, address: saved.address, city: saved.city || '', lat: null, lng: null });
      setEditingSaved(false);
      return;
    }
    if (mode === 'current') {
      onChange({ mode });
      locate();
      return;
    }
    onChange({ mode });
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setStatus('Location is not available on this device — pick the spot on the map instead.');
      onChange({ mode: 'other' });
      return;
    }
    setLocating(true);
    setStatus('Finding your location…');
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords;
        try {
          const r = await api.get('/api/reverse', { lat, lng });
          onChange({ mode: 'current', lat, lng, address: r.label || '', city: r.city || '' });
          setStatus('');
          // Only skip ahead once there is an address worth reviewing.
          if (r.label) onResolved?.();
        } catch {
          // The pin is still good even when the lookup is down — keep it and let
          // the patient describe the place themselves.
          onChange({ mode: 'current', lat, lng });
          setStatus('Location set, but we could not look up the address. Please type it below.');
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        setStatus('Could not read your location. Check browser permissions, or pick the spot on the map.');
      },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  // The map emits { lat, lng, address, city, state }; the booking only stores a
  // free-text address and city, so state is ignored here.
  const fromMap = ({ lat, lng, address, city }) => {
    const patch = { lat, lng };
    if (address != null) patch.address = address;
    if (city) patch.city = city;
    onChange(patch);
  };

  return (
    <>
      <span className="label">Choose the visit location</span>
      <div className="loc-choices mt-1" role="radiogroup" aria-label="Visit location">
        {choices.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={value.mode === c.id}
            className="loc-choice"
            data-active={value.mode === c.id ? 'true' : 'false'}
            onClick={() => choose(c.id)}
          >
            <span className={`icon-tile ${value.mode === c.id ? '' : 'icon-tile-neutral'}`} style={{ width: 38, height: 38 }}>
              <Icon name={c.icon} size={18} />
            </span>
            <span className="grow">
              <span className="nm">{c.name}</span>
              <span className="ct">{c.hint}</span>
            </span>
          </button>
        ))}
      </div>

      {status && (
        <p className="tiny mt-1" style={{ color: locating ? 'var(--text-3)' : 'var(--warning, var(--text-2))' }}>
          {locating && <span className="spinner" style={{ width: 11, height: 11, marginRight: 6 }} />}
          {status}
        </p>
      )}

      {value.mode === 'saved' && (
        <div className="banner banner-info mt-2">
          <Icon name="home" />
          <span className="grow">
            {value.address}
            {value.city ? `, ${value.city}` : ''}
          </span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditingSaved((v) => !v)}>
            {editingSaved ? 'Done' : 'Edit'}
          </button>
        </div>
      )}

      {value.mode === 'current' && !locating && (
        <p className="tiny dim mt-1">{HERE_HINT}</p>
      )}

      {/* MapPicker carries its own instructions, so this Field adds none. */}
      {value.mode === 'other' && (
        <Field label="Pin the exact spot">
          <MapPicker value={{ lat: value.lat, lng: value.lng, address: value.address }} onChange={fromMap} />
        </Field>
      )}

      {/* The address stays editable in every mode except an untouched saved one:
          a pin gets you to the building, a flat number gets you to the door. */}
      {(value.mode !== 'saved' || editingSaved) && (
        <>
          <Field
            label="Visit address"
            id="addr"
            hint="Include flat/house number and a landmark."
            // The map opens with its pin at a default centre, so the ask is to
            // confirm the real spot rather than to "drop" a pin that looks set.
            error={
              value.mode === 'other' && value.lat == null
                ? 'Tap the map or drag the pin to confirm the exact spot.'
                : undefined
            }
          >
            <textarea
              id="addr"
              value={value.address || ''}
              onChange={(e) => onChange({ address: e.target.value })}
              placeholder="Flat 4B, Green Residency, near St. Mary's Church…"
              required
            />
          </Field>
          <Field label="City" id="cty">
            <input id="cty" value={value.city || ''} onChange={(e) => onChange({ city: e.target.value })} />
          </Field>
        </>
      )}
    </>
  );
}
